import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQueryHistory, deleteQuery, deleteConversation } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import DataVisualization from '../components/DataVisualization';
import { 
  History, 
  Search, 
  Trash2, 
  MessageSquare,
  Clock,
  Database,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  User,
  Sparkles,
  ExternalLink,
  BarChart3
} from 'lucide-react';

const HistoryPage = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedConversations, setExpandedConversations] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [showCharts, setShowCharts] = useState({});

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const data = await getQueryHistory(200);
      setHistory(data);
    } catch (error) {
      toast.error('Failed to fetch query history');
    } finally {
      setLoading(false);
    }
  };

  // Group queries by conversation
  const conversations = useMemo(() => {
    const grouped = {};
    
    history.forEach(item => {
      const convId = item.conversation_id || item.id;
      if (!grouped[convId]) {
        grouped[convId] = {
          id: convId,
          messages: [],
          firstQuery: item.query,
          lastUpdated: item.created_at,
          connectionName: item.connection_name
        };
      }
      grouped[convId].messages.push(item);
      
      // Update lastUpdated if this message is newer
      if (new Date(item.created_at) > new Date(grouped[convId].lastUpdated)) {
        grouped[convId].lastUpdated = item.created_at;
      }
    });
    
    // Sort messages within each conversation by time
    Object.values(grouped).forEach(conv => {
      conv.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });
    
    // Sort conversations by most recent
    return Object.values(grouped)
      .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
  }, [history]);

  // Filter conversations by search term
  const filteredConversations = useMemo(() => {
    if (!searchTerm) return conversations;
    
    const term = searchTerm.toLowerCase();
    return conversations.filter(conv => 
      conv.messages.some(msg => 
        msg.query?.toLowerCase().includes(term) ||
        (msg.analysis || msg.response || '').toLowerCase().includes(term)
      )
    );
  }, [conversations, searchTerm]);

  const handleDeleteConversation = async (convId) => {
    try {
      await deleteConversation(convId);
      setHistory(prev => prev.filter(h => (h.conversation_id || h.id) !== convId));
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const toggleConversation = (convId) => {
    setExpandedConversations(prev => ({
      ...prev,
      [convId]: !prev[convId]
    }));
  };

  const toggleChart = (msgId) => {
    setShowCharts(prev => ({
      ...prev,
      [msgId]: !prev[msgId]
    }));
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const continueConversation = (conv) => {
    navigate('/query', { state: { conversationId: conv.id } });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-12 w-full max-w-md" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Query History
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            View your past AI conversations
          </p>
        </div>
        <div className="text-sm text-gray-400">
          {filteredConversations.length} {filteredConversations.length === 1 ? 'conversation' : 'conversations'}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search conversations..."
          className="pl-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"
          data-testid="search-history-input"
        />
      </div>

      {/* Conversations List */}
      {filteredConversations.length > 0 ? (
        <div className="space-y-4">
          {filteredConversations.map((conv) => (
            <Card 
              key={conv.id} 
              className="bg-white border-gray-200 hover:shadow-md transition-all overflow-hidden"
            >
              {/* Conversation Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleConversation(conv.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-[#D4AF37] shrink-0" />
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {conv.firstQuery}
                      </span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
                        {conv.messages.length} {conv.messages.length === 1 ? 'message' : 'messages'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(conv.lastUpdated)}</span>
                      </div>
                      {conv.connectionName && (
                        <div className="flex items-center gap-1">
                          <Database className="w-3 h-3" />
                          <span>{conv.connectionName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); continueConversation(conv); }}
                      className="text-[#D4AF37] hover:text-[#B8960F] hover:bg-[#D4AF37]/10"
                      data-testid={`continue-${conv.id}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Continue
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-400 hover:text-red-600"
                          data-testid={`delete-${conv.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-white border-gray-200">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-gray-900">Delete Conversation?</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-500">
                            This will permanently delete all {conv.messages.length} messages in this conversation.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-gray-200 text-gray-700 hover:bg-gray-50">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteConversation(conv.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); toggleConversation(conv.id); }}
                      className="text-gray-400 hover:text-gray-700"
                    >
                      {expandedConversations[conv.id] ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Expanded Conversation Thread */}
              {expandedConversations[conv.id] && (
                <div className="border-t border-gray-100 bg-gray-50/50">
                  <ScrollArea className="max-h-[500px]">
                    <div className="p-4 space-y-4">
                      {conv.messages.map((msg, idx) => (
                        <div key={msg.id} className="space-y-3">
                          {/* User Message */}
                          <div className="flex gap-3">
                            <div className="w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center shrink-0">
                              <User className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-700">You</span>
                                <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                              </div>
                              <p className="text-sm text-gray-800 bg-white p-3 rounded-lg border border-gray-200 inline-block">
                                {msg.query}
                              </p>
                            </div>
                          </div>
                          
                          {/* AI Response */}
                          <div className="flex gap-3">
                            <div className="w-7 h-7 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0">
                              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-700">MyAI Analysis</span>
                                <button
                                  onClick={() => copyToClipboard(msg.analysis || msg.response || '', msg.id)}
                                  className="text-gray-400 hover:text-gray-600 p-0.5"
                                >
                                  {copiedId === msg.id ? (
                                    <Check className="w-3 h-3 text-emerald-500" />
                                  ) : (
                                    <Copy className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                              <div className="text-sm text-gray-700 bg-white p-3 rounded-lg border border-gray-200">
                                <div className="whitespace-pre-wrap">
                                  {msg.analysis || msg.response || 'No response'}
                                </div>
                                
                                {/* Visualization toggle */}
                                {msg.visualization && (
                                  <div className="mt-3 pt-3 border-t border-gray-100">
                                    <button
                                      onClick={() => toggleChart(msg.id)}
                                      className="text-xs text-[#D4AF37] hover:text-[#B8960F] flex items-center gap-1"
                                    >
                                      <BarChart3 className="w-3 h-3" />
                                      {showCharts[msg.id] ? 'Hide chart' : 'Show chart'}
                                    </button>
                                    {showCharts[msg.id] && (
                                      <DataVisualization visualization={msg.visualization} />
                                    )}
                                  </div>
                                )}
                                
                                {/* Row count */}
                                {msg.row_count > 0 && (
                                  <div className="mt-2 text-xs text-gray-400">
                                    {msg.row_count} rows returned
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Separator between message pairs */}
                          {idx < conv.messages.length - 1 && (
                            <div className="border-t border-dashed border-gray-200 my-4" />
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <History className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No matching conversations' : 'No conversations yet'}
            </h3>
            <p className="text-gray-500 text-sm text-center max-w-sm">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : 'Your AI conversations will appear here'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HistoryPage;
